/* eslint-disable no-else-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import type { HTMLAttributes } from 'react';
import type { TSchema } from 'types/items';
import { DropzoneArea } from 'components/MuiFileDropzone';
import {
  DataGrid,
  GridCellModes,
  GridCellModesModel,
  gridClasses,
  GridCellParams,
  GridColDef,
  GridColType,
} from '@mui/x-data-grid';
import { baseDialogStyle } from 'containers/Base/styles';
import type { WorkingState } from '../../Data/data.types';
import { TParsedFieldWithFieldName } from '../utilities';

export interface ImportDialogProps<T extends any[] = any[]>
  extends HTMLAttributes<HTMLDivElement> {
  fields: TParsedFieldWithFieldName[];
  handleAdd: (newRecords: T) => void;
  handleCancel: () => void;
  itemLabel?: string;
  open: boolean;
  schema: TSchema<any>;
  workingState: WorkingState;
}

export type ImportDialogComponent<T extends any[] = any[]> = FC<
  ImportDialogProps<T>
>;

const getColumnType = (field: any) => {
  if (field.type === 'bool') {
    return 'boolean';
  } else if (field.type === 'enum') {
    return 'singleSelect';
  } else {
    return field.type as GridColType;
  }
};

const getImportStatusChipColor = (status: string) => {
  switch (status) {
    case 'Waiting':
      return 'silver';
    case 'Importing':
      return 'darkorange';
    case 'Success':
      return 'lightgreen';
    case 'Error':
      return 'lightcoral';
    default:
      return 'default';
  }
};

const ImportDialog: ImportDialogComponent = ({
  fields,
  handleAdd,
  handleCancel,
  itemLabel,
  open,
  schema,
  workingState,
}) => {
  const { classes } = baseDialogStyle();

  // State
  const [activeStep, setActiveStep] = React.useState(0);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [columns, setColumns] = React.useState<GridColDef[]>([]);
  const [columnsImporting, setColumnsImporting] = React.useState<GridColDef[]>(
    []
  );
  const [importComplete, setImportComplete] = useState(false);
  const [rows, setRows] = React.useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const fileLoadIdRef = useRef(0);

  // Handlers
  const handleBack = () => {
    // If the import has already been completed and the user hits back, clear out
    // any of the 'Success' rows and reset the importComplete flag.  Also reset the
    // importStatus and importMessage columns.
    if (importComplete) {
      const updatedRows = rows
        .filter((r) => r.importStatus === 'Error')
        .map((row) => ({ ...row, importStatus: 'Waiting', importMessage: '' }));
      setRows(updatedRows);
      setImportComplete(false);
    }

    setActiveStep(activeStep - 1);
  };
  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => ({
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    []
  );
  const handleCellModesModelChange = React.useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );
  const handleImportTemplate = () => {
    const templateFields = fields.filter((f) => f.required !== 'A');
    // Develop a CSV file with each field name as a header and an empty row
    // for the user to fill in with the data they want to import.  Then present
    // as a download to the user.
    let csv = templateFields.map((f: any) => f.fieldName).join(',');
    csv += '\n';

    // Also give the user some guidance on acceptable values.  If the field type
    // is an enum, then list out the acceptable values by combining schema?.fields?[field.fieldName]?.values
    // with schema?.fields?[field.fieldName]?.valueLabels.  If the field type is a bool, then just say
    // 'true' or 'false' are acceptable values.  If the field type is a datetime, then just say 'YYYY-MM-DD HH:MM:SS'
    // is the acceptable format.  If the field type is a string, then just say 'Any string value up to X characters
    // is acceptable' where X is the max length of the field.
    templateFields.forEach((f: any) => {
      if (f.type === 'enum') {
        const values = schema?.fields?.[f.fieldName]?.values;
        const valueLabels = schema?.fields?.[f.fieldName]?.valueLabels;
        const valueStrings = values?.map(
          (v, i) => `${v} (${valueLabels?.[i]})`
        );
        csv += `Any of: ${valueStrings?.join(' ')}`;
      } else if (f.type === 'bool') {
        csv += 'true or false';
      } else if (f.type === 'datetime') {
        csv += 'YYYY-MM-DD HH:MM:SS';
      } else {
        csv += `Any string value up to ${f.size} characters`;
      }
      csv += ',';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Harmoni ${itemLabel} Import.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleUploadFileDrop = (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    // DropzoneArea passes the full accumulated selection on every drop, so we
    // rebuild the preview from all files each time.
    setUploadFiles(files);
    const loadId = ++fileLoadIdRef.current;

    const parseCsvHeader = (
      contents: string | ArrayBuffer | null | undefined
    ): string[] => {
      const firstLine = (contents as string).split('\n')[0] ?? '';
      return firstLine.replaceAll('\r', '').split(',');
    };

    // Parse one CSV's contents into preview rows (does not touch state).
    const parseFileContents = (
      contents: string | ArrayBuffer | null | undefined,
      headers: string[]
    ): any[] => {
      const lines = (contents as string).split('\n');
      return lines.slice(1).map((line) => {
        const row = line.replaceAll('\r', '').split(',');

        // Add a unique 'id' column to each row for the DataGrid
        row.unshift(Math.random().toString(36).substr(2, 9));

        const parsed = headers.reduce((acc: any, header, i) => {
          acc[header] = row[i];
          return acc;
        }, {});
        parsed.importStatus = 'Waiting';
        return parsed;
      });
    };

    const readFile = (file: File) =>
      new Promise<{ header: string; rows: any[] }>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const contents = e?.target?.result;
          const header = parseCsvHeader(contents);
          const headers: string[] = ['id'].concat(header);
          resolve({
            header: header.join(','),
            rows: parseFileContents(contents, headers),
          });
        };
        reader.onerror = () => resolve({ header: '', rows: [] });
        reader.readAsText(file);
      });

    // Read every file, then set rows to the combined result so multi-file
    // imports keep all rows instead of the last reader clobbering the rest.
    Promise.all(files.map(readFile)).then((parsedFiles) => {
      if (loadId !== fileLoadIdRef.current) {
        return;
      }

      const headerKeys = parsedFiles.map((f) => f.header).filter(Boolean);
      if (headerKeys.length > 1 && new Set(headerKeys).size > 1) {
        // eslint-disable-next-line no-alert
        window.alert(
          'All CSV files must use the same column headers. Remove mismatched files and try again.'
        );
        setUploadFiles([]);
        setRows([]);
        return;
      }

      setRows(parsedFiles.flatMap((f) => f.rows));
    });
    // setActiveStep(activeStep + 1);
  };
  const onImport = () => {
    const preparedNewRecords: any[] = [];
    const updatedRows: any[] = [];
    // Build properly defined new record objects to pass to the handleAddRecord function.
    // Most everything should be good to go as-is, but for any enum field types, we need to
    // extract the 'id' property from the value associated with the field and use that as the
    // value for the new record.
    rows.forEach((row) => {
      const preparedNewRecord = fields.reduce((acc, field) => {
        if (field.type === 'enum' && field.fieldName) {
          const index =
            schema.fields[field.fieldName]?.valueLabels?.indexOf(
              row[field.fieldName]
            ) || -1;
          if (index > 0) {
            acc[field.fieldName] =
              schema.fields[field.fieldName]?.values?.[index];
          } else {
            acc[field.fieldName] = row[field.fieldName];
          }
        } else if (field.type === 'bool') {
          acc[field.fieldName] = row[field.fieldName] === 'true';
        } else {
          acc[field.fieldName] = row[field.fieldName];
        }
        return acc;
      }, {} as { [key: string]: any });
      updatedRows.push({ ...row, importStatus: 'Importing' });
      preparedNewRecords.push(preparedNewRecord);
    });
    setRows(updatedRows);
    console.log(preparedNewRecords);
    handleAdd(preparedNewRecords);
    setSaving(true);
  };
  const onCancel = () => {
    handleCancel();
  };

  // Hooks
  useMemo(() => {
    if (schema?.hierarchy === undefined) {
      return;
    }
    const { primaryKey } = schema.hierarchy;
    if (fields) {
      // For columnsImporting, add additional columns for 'importStatus' and 'importMessage'
      const gridColumnsImporting: GridColDef[] = [
        {
          field: 'importStatus',
          headerName: 'Import Status',
          type: 'string',
          minWidth: 150,
          editable: false,
          renderCell: (params) => (
            <Chip
              icon={
                params.value === 'Importing' ? <CloudSyncIcon /> : undefined
              }
              label={params.value}
              sx={{ backgroundColor: getImportStatusChipColor(params.value) }}
            />
          ),
        },
      ];

      const gridColumns = fields
        .filter((f) => f.required !== 'A')
        .map((field) => ({
          ...field,
          editable: field.fieldName !== primaryKey && field.editable === 'true',
          field: field.fieldName,
          headerName: field.label,
          type: getColumnType(field),
          valueFormatter: (params: any) => {
            let resp = params;
            if (field.type === 'datetime' && params) {
              resp = new Date(params).toLocaleString();
            } else if (field.type === 'enum') {
              // If the column type is an enum, we want to return the valueLabel of the enum.
              // To determine this, you need to find the index of the column value within the
              // values array and then return the same indexed value from the valueLabels array.
              const index: number | undefined =
                schema.fields[field.fieldName]?.values?.indexOf(params);
              if (index === undefined) {
                resp = null;
              } else {
                resp = schema.fields[field.fieldName]?.valueLabels?.[index];
              }
            }
            return resp;
          },
          valueOptions: () => {
            if (field.type === 'enum') {
              return schema.fields[field.fieldName]?.valueLabels || [];
            } else {
              return undefined;
            }
          },
        }));
      setColumns(gridColumns);

      // For gridColumnsImporting, add all of the columns from gridColumns and then add the 'importMessage' column
      gridColumnsImporting.push(...gridColumns);
      gridColumnsImporting.push({
        field: 'importMessage',
        headerName: 'Import Message',
        minWidth: 500,
        type: 'string',
        editable: false,
      });
      setColumnsImporting(gridColumnsImporting);
    }
  }, [schema]);

  useEffect(() => {
    // Check if we have results and rows that need status updates
    const hasResults =
      workingState.results &&
      Array.isArray(workingState.results) &&
      workingState.results.length > 0;
    const hasRowsWithImportingStatus = rows.some(
      (row) => row.importStatus === 'Importing'
    );

    // Process results if we have them and there are rows that need updating
    if (hasResults && hasRowsWithImportingStatus && !workingState?.adding) {
      // workingState.results contains the results of each row that was submitted for import.
      // Match each row in the rows array to the cooresponding result in workingState.results
      // and update the importStatus and importMessage columns accordingly.  importStatus should
      // say 'Success' if the result was successful, 'Error' if the result was not successful.
      // importMessage should contain the value from the 'message' property if it is present.
      const updatedRows = rows.map((row, i) => {
        // Each row should have a corresponding result at the same index
        const result = workingState.results[i];

        // Handle case where result might be undefined
        if (!result) {
          return {
            ...row,
            importStatus: 'Error',
            importMessage: 'No result data available',
          };
        }

        // Check for success property - if it doesn't exist, assume success if we have a result
        let importStatus;
        let importMessage = '';

        if ('success' in result) {
          importStatus = result.success ? 'Success' : 'Error';
          importMessage = result.message || '';
        } else if (result.error || result.errorMessage) {
          importStatus = 'Error';
          importMessage =
            result.error || result.errorMessage || 'Import failed';
        } else {
          // Assume success if we have a valid result object without error indicators
          importStatus = 'Success';
          importMessage = 'Record imported successfully';
        }

        return { ...row, importStatus, importMessage };
      });

      setImportComplete(true);
      setRows(updatedRows);
      setSaving(false);
    } else if (
      !hasResults &&
      hasRowsWithImportingStatus &&
      !workingState?.adding &&
      saving
    ) {
      // Handle case where import process completed but no results were returned (likely an error)
      const updatedRows = rows.map((row) => ({
        ...row,
        importStatus: 'Error',
        importMessage: 'Import failed - check error messages above',
      }));

      setImportComplete(true);
      setRows(updatedRows);
      setSaving(false);
    }
  }, [workingState, saving, rows]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="xl"
      sx={{ '& .MuiDialog-paper': { width: '100%', height: '100%' } }}
      aria-labelledby="new-record-dialog-title"
      aria-describedby="new-record-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Import {itemLabel}s</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          <Step>
            <StepLabel>Upload File</StepLabel>
          </Step>
          <Step>
            <StepLabel>Preview Data</StepLabel>
          </Step>
          <Step>
            <StepLabel>Finalize Import</StepLabel>
          </Step>
        </Stepper>
        {activeStep === 0 && (
          <div style={{ marginTop: 20 }}>
            <DropzoneArea
              fileObjects={uploadFiles}
              acceptedFiles={['text/csv']}
              maxFileSize={134_217_728} // 128MB,
              onChange={handleUploadFileDrop}
              showFileNames
            />
            <Button color="primary" onClick={handleImportTemplate}>
              Download Import Tempate
            </Button>
          </div>
        )}

        {activeStep === 1 && (
          <DataGrid
            autosizeOnMount
            rows={rows}
            columns={columns}
            cellModesModel={cellModesModel}
            onCellModesModelChange={handleCellModesModelChange}
            onCellClick={handleCellClick}
            hideFooter
            processRowUpdate={(newRow, oldRow) => {
              const updatedRow = { ...newRow, isNew: false };
              const isDirty = Object.keys(updatedRow).some(
                (key) => updatedRow[key] !== oldRow[key]
              );
              updatedRow.isDirty = isDirty;
              setRows(
                rows.map((row) => (row.id === newRow.id ? updatedRow : row))
              );
              return { ...updatedRow, isDirty };
            }}
            sx={{
              borderTop: 0,
              borderLeft: 0,
              borderRight: 0,
              marginTop: 2,
              height: 'calc(100vh - 250px)',
              [`& .${gridClasses.columnHeaders}`]: {
                paddingLeft: '0px',
                paddingRight: `1px`,
              },
              [`& .${gridClasses.columnHeaderTitle}`]: {
                textTransform: 'uppercase',
                color: 'rgb(123, 128, 154)',
                fontSize: '0.65rem',
                fontWeight: 700,
              },
            }}
          />
        )}
        {activeStep === 2 && (
          <div>
            <DataGrid
              autosizeOnMount
              rows={rows}
              columns={columnsImporting}
              cellModesModel={cellModesModel}
              onCellModesModelChange={handleCellModesModelChange}
              onCellClick={handleCellClick}
              hideFooter
              sx={{
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                marginTop: 2,
                height: 'calc(100vh - 250px)',
                [`& .${gridClasses.columnHeaders}`]: {
                  paddingLeft: '0px',
                  paddingRight: `1px`,
                },
                [`& .${gridClasses.columnHeaderTitle}`]: {
                  textTransform: 'uppercase',
                  color: 'rgb(123, 128, 154)',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                },
              }}
            />
          </div>
        )}

        <div className={classes.progressContainer}>
          {workingState?.adding && (
            <div className={classes.progressInner}>
              <CircularProgress size={25} />
              <span className={classes.progressLabel}>
                Adding {itemLabel}...
              </span>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {(activeStep < 2 || !importComplete) && (
          <Button onClick={onCancel} color="primary">
            Cancel
          </Button>
        )}
        {(activeStep < 2 ||
          !importComplete ||
          rows.some((r) => r.importStatus === 'Error')) && (
          <Button
            onClick={() => handleBack()}
            color="primary"
            disabled={activeStep === 0}
          >
            Back
          </Button>
        )}
        {activeStep < 2 && (
          <Button
            onClick={() => setActiveStep(activeStep + 1)}
            color="primary"
            disabled={uploadFiles.length === 0}
          >
            Next
          </Button>
        )}
        {activeStep === 2 && !importComplete && (
          <Button
            onClick={() => onImport()}
            color="primary"
            variant="contained"
            disabled={rows.length === 0 || workingState?.adding}
          >
            Import {rows.length} {itemLabel}s
          </Button>
        )}
        {activeStep === 2 && importComplete && (
          <Button onClick={onCancel} color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
